import React from 'react';
import PropTypes from 'prop-types';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isFunction from 'lodash/isFunction';

import CleanseAddressModule from '../../cleanseAddress';
import { WrappedButton, WrappedButtonType } from '../button';
import { WrappedInputTextarea } from '../input';
import LabelWrapper from '../LabelWrapper.component';

const { CleanseAddress, CleanseAddressStatus } = CleanseAddressModule;

export class AddressField extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			// formatted address string
			formattedAddress: '',

			// cleansed result
			cleanseStatus: CleanseAddressStatus.NotCleansed,

			// `true` to trigger address cleansing flow
			triggerCleanseAddress: false,
		};
	}

	componentDidMount() {
		const { address, triggerCleanseAddress } = this.props;

		this.updateState(address, triggerCleanseAddress);
	}

	componentDidUpdate(prevProps) {
		const { address, triggerCleanseAddress } = this.props;

		if (
			prevProps.address !== address ||
			prevProps.triggerCleanseAddress !== triggerCleanseAddress
		) {
			// check if address should be cleansed
			const shouldTriggerCleanseAaddress =
				prevProps.triggerCleanseAddress !== triggerCleanseAddress &&
				triggerCleanseAddress === true;

			this.updateState(address, shouldTriggerCleanseAaddress);
		}
	}

	/**
	 * Return formatted address
	 * @param {object} address
	 * @returns {string} formatted address
	 */
	buildFormattedAddress = address => {
		const {
			companyName,
			name,
			addressLine1,
			addressLine2,
			addressLine3,
			city,
			stateProvince,
			postalCode,
		} = isObject(address) ? address : {};
		let formattedAddress = '';

		if (isString(name) && name !== '') {
			formattedAddress += `${name}\n`;
		}

		if (isString(companyName) && companyName !== '') {
			formattedAddress += `${companyName}\n`;
		}

		if (isString(addressLine1) && addressLine1 !== '') {
			formattedAddress += `${addressLine1}\n`;
		}

		if (isString(addressLine2) && addressLine2 !== '') {
			formattedAddress += `${addressLine2}\n`;
		}

		if (isString(addressLine3) && addressLine3 !== '') {
			formattedAddress += `${addressLine3}\n`;
		}

		if (isString(city) && city !== '') {
			formattedAddress += city;
		}

		if (isString(stateProvince) && stateProvince !== '') {
			formattedAddress += `, ${stateProvince}`;
		}

		if (isString(postalCode) && postalCode !== '') {
			formattedAddress += ` ${postalCode}`;
		}

		return formattedAddress;
	};

	/**
	 * Render CleanseAddress component to start address cleansing flow
	 */
	cleanseAddress = () => {
		this.setState({
			triggerCleanseAddress: true,
		});
	};

	/**
	 * Return the cleanse address result back to the caller
	 * @param {object} response
	 */
	cleanseAddressCallback = response => {
		const { name } = this.props;
		const { cleanseStatus, cleanseMessage, address = {} } = response;

		switch (cleanseStatus) {
			case CleanseAddressStatus.Cleansed:
			case CleanseAddressStatus.InvalidRecipient:
				this.setState(
					{
						formattedAddress: address.formattedAddress,
						cleanseStatus,
						triggerCleanseAddress: false,
					},
					() => {
						this.notifyParent('onCleansed', {
							cleanseStatus,
							cleanseMessage,
							address,
							name,
						});
					}
				);
				break;
			case CleanseAddressStatus.EmptyAddress:
			case CleanseAddressStatus.InvalidCandidateAddress:
			case CleanseAddressStatus.MultipleMatch:
			case CleanseAddressStatus.NotFound:
			case CleanseAddressStatus.MalformedAddress:
			default:
				this.setState(
					{
						cleanseStatus,
						triggerCleanseAddress: false,
					},
					() => {
						this.notifyParent('onCleansed', {
							cleanseStatus,
							cleanseMessage,
							name,
						});
					}
				);
				break;
		}
	};

	/**
	 * Notify the caller about the cleanse address result.
	 * @param {string} eventName 'onChange' or 'onCleansed
	 * @param {object} data includes "cleanseStatus", "name" and "address" (if the status is "Cleansed")
	 */

	notifyParent = (eventName, data) => {
		const { onCleansed, onChange } = this.props;

		if (eventName === 'onCleansed' && isFunction(onCleansed)) {
			onCleansed(data);
		} else if (eventName === 'onChanged' && isFunction(onChange)) {
			onChange(data);
		}
	};

	/**
	 * Update `formattedAddress` state.
	 * @param {object} address Itemized address object.
	 * @param {boolean} triggerCleanseAddress `true` to trigger address cleansing flow.
	 */
	updateState = (address, triggerCleanseAddress) => {
		const { formattedAddress } = this.state;
		const newFormattedAddress = this.buildFormattedAddress(address);

		if (formattedAddress !== newFormattedAddress) {
			this.setState(
				{
					formattedAddress: newFormattedAddress,
					cleanseStatus: CleanseAddressStatus.NotCleansed,
				},
				() => {
					if (triggerCleanseAddress) {
						this.cleanseAddress();
					}
				}
			);
		} else if (triggerCleanseAddress) {
			this.cleanseAddress();
		}
	};

	onChange = ({ target }) => {
		const { name, onChange } = this.props;
		const { value } = target;

		this.setState({
			formattedAddress: value,
		});

		if (onChange) {
			this.notifyParent('onChanged', {
				name,
				value,
			});
		}
	};

	render() {
		const { placeholder, isInputInvalid, dataqa } = this.props;
		const { formattedAddress, cleanseStatus, triggerCleanseAddress } = this.state;

		return (
			<div className="common-ui-address-field">
				{triggerCleanseAddress && (
					<CleanseAddress
						fullAddress={formattedAddress}
						callback={this.cleanseAddressCallback}
						validateRecipient
					/>
				)}
				<WrappedInputTextarea
					rows={5}
					cols={31}
					maxLength={200}
					value={formattedAddress}
					isInputInvalid={isInputInvalid}
					placeholder={placeholder}
					onChange={this.onChange}
					onBlur={this.cleanseAddress}
					dataqa={`${dataqa}-addressfield`}
				/>
				{cleanseStatus === CleanseAddressStatus.MultipleMatch && (
					<WrappedButton
						tooltip="The USPS address standardization system could not find this exact address.  Click here if you wish to view a list of possible matching addresses."
						label="View Suggested Address Corrections"
						buttonType={WrappedButtonType.TertiaryGrey}
						onClick={this.cleanseAddress}
						dataqa="addressfield-view-suggested-corrections"
					/>
				)}
			</div>
		);
	}
}

AddressField.propTypes = {
	// address that needs to be cleansed
	address: PropTypes.object,

	// placeholder for the address input field
	placeholder: PropTypes.string,

	// field name will be passed back to the caller when the address is cleansed
	name: PropTypes.string,

	// This allows the caller to trigger address cleansing (address/validate request)
	triggerCleanseAddress: PropTypes.bool,

	// The handler to notify the caller the ui value change
	onChange: PropTypes.func,

	// The handler to notify the caller the cleansed result
	onCleansed: PropTypes.func,
};

export const AddressFieldWithLabel = LabelWrapper(AddressField, 'common-ui-address-field-wrapper');
